// 🎯 A/B/C/D 등급 시스템 상수 - 단일 소스 (Single Source of Truth)
// 모든 등급 관련 코드는 이 파일에서 import하여 사용

/**
 * 등급 값 배열 (타입 추론용)
 */
export const GRADE_VALUES = ['A', 'B', 'C', 'D', 'E'] as const;

/**
 * 등급 값 타입
 */
export type GradeValue = typeof GRADE_VALUES[number];

/**
 * 등급 상세 정보 인터페이스
 */
export interface GradeInfo {
  value: GradeValue;
  label: string;
  color: string;
  icon: string;
  description: string;
}

/**
 * 🎨 등급별 색상 (접근성 고려)
 */
export const GRADE_COLORS: Record<GradeValue, string> = {
  A: '#10b981', // Green-500 (최고 등급)
  B: '#3b82f6', // Blue-500 (양호 등급)
  C: '#f59e0b', // Orange-500 (보통 등급)
  D: '#ef4444', // Red-500 (불량 등급)
  E: '#6b7280', // Gray-500 (최저 등급)
};

/**
 * 📝 등급별 라벨
 */
export const GRADE_LABELS: Record<GradeValue, string> = {
  A: 'A등급',
  B: 'B등급',
  C: 'C등급',
  D: 'D등급',
  E: 'E등급',
};

/**
 * 🏷️ 등급별 짧은 설명 (알림톡 메시지용)
 */
export const GRADE_DESCRIPTIONS_SHORT: Record<GradeValue, string> = {
  A: '최고 등급',
  B: '양호 등급',
  C: '보통 등급',
  D: '불량 등급',
  E: '최저 등급',
};

/**
 * 📋 등급별 상세 설명
 */
export const GRADE_DESCRIPTIONS: Record<GradeValue, string> = {
  A: '기능 정상, 외관 흠집 또는 잔상 없음',
  B: '기능 정상, 생활기스·미세 흠집 또는 약한 잔상',
  C: '기능 정상, 뚜렷한 흠집·찍힘 또는 중잔상',
  D: 'LCD 불량, 전원, 카메라, Face ID 등 주요 기능 불량',
  E: '전원 불가, 침수, 부품용 등 정상 사용이 불가능한 상태',
};

/**
 * 🔣 등급별 아이콘 (이모지)
 */
export const GRADE_ICONS: Record<GradeValue, string> = {
  A: '🟢',
  B: '🔵',
  C: '🟡',
  D: '🔴',
  E: '⚫',
};

/**
 * 📊 등급 리스트 (UI Select/MenuItem용)
 * - 관리자 페이지에서 기기별 등급 선택 시 사용
 */
export const GRADE_LIST: GradeInfo[] = [
  {
    value: 'A',
    label: 'A등급',
    color: GRADE_COLORS.A,
    icon: GRADE_ICONS.A,
    description: GRADE_DESCRIPTIONS.A,
  },
  {
    value: 'B',
    label: 'B등급',
    color: GRADE_COLORS.B,
    icon: GRADE_ICONS.B,
    description: GRADE_DESCRIPTIONS.B,
  },
  {
    value: 'C',
    label: 'C등급',
    color: GRADE_COLORS.C,
    icon: GRADE_ICONS.C,
    description: GRADE_DESCRIPTIONS.C,
  },
  {
    value: 'D',
    label: 'D등급',
    color: GRADE_COLORS.D,
    icon: GRADE_ICONS.D,
    description: GRADE_DESCRIPTIONS.D,
  },
  {
    value: 'E',
    label: 'E등급',
    color: GRADE_COLORS.E,
    icon: GRADE_ICONS.E,
    description: GRADE_DESCRIPTIONS.E,
  },
];

/**
 * 🔧 등급 유효성 검사 함수
 */
export function isValidGrade(value: unknown): value is GradeValue {
  return typeof value === 'string' && GRADE_VALUES.includes(value as GradeValue);
}

/**
 * 🎨 등급에 해당하는 색상 반환 (fallback 포함)
 */
export function getGradeColor(grade: string | null | undefined): string {
  if (!grade || !isValidGrade(grade)) {
    return '#90a4ae'; // 기본 회색
  }
  return GRADE_COLORS[grade];
}

/**
 * 🏷️ 등급에 해당하는 라벨 반환 (fallback 포함)
 */
export function getGradeLabel(grade: string | null | undefined): string {
  if (!grade || !isValidGrade(grade)) {
    return '등급 미정';
  }
  return GRADE_LABELS[grade];
}

/**
 * 🔣 등급에 해당하는 아이콘 반환 (fallback 포함)
 */
export function getGradeIcon(grade: string | null | undefined): string {
  if (!grade || !isValidGrade(grade)) {
    return '⚪';
  }
  return GRADE_ICONS[grade];
}

/**
 * 카테고리에 따라 표시할 등급 목록 반환
 * E등급은 스마트폰 카테고리에서만 표시
 */
export function getGradesForCategory(category?: string): GradeValue[] {
  if (category === '스마트폰' || category === 'smartphone' || category === '핸드폰') {
    return ['A', 'B', 'C', 'D', 'E'];
  }
  return ['A', 'B', 'C', 'D'];
}

